import React from 'react';
import { useIntl } from '@leagueplatform/locales';
import { usePageViewAnalytics } from '@leagueplatform/analytics';
import type { UserProfileAggregate } from '@leagueplatform/user-profile-api';
import {
  Box,
  Button,
  Checkbox,
  HeadingText,
  ParagraphText,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { AccountSetupField } from './account-setup-field.component';
import { AvatarPicker } from './avatar-picker.component';
import { useAccountSetup } from './use-account-setup.hook';

const CARD_WIDTH = '708px';

interface AccountSetupProps {
  onComplete: () => void;
  initialProfile: UserProfileAggregate;
  // Per-tenant avatar art — the consuming app supplies its own, the same way
  // it supplies its own `BasePage` shell around this component.
  avatarPlaceholderSrc: string;
  avatarEditBadgeSrc: string;
}

// Headless: business logic + the card's inner content only. The consuming
// app wraps this in its own `BasePage` (nav bar / footer / live chat are all
// app-local, not available here) — see e.g. GenericErrorScreen for the same
// shared-module convention.
export const AccountSetup = ({
  onComplete,
  initialProfile,
  avatarPlaceholderSrc,
  avatarEditBadgeSrc,
}: AccountSetupProps) => {
  usePageViewAnalytics({ screen_name: 'Account Setup' });
  const { formatMessage } = useIntl();
  const {
    fullName,
    fields,
    values,
    errors,
    onFieldChange,
    marketingOptIn,
    setMarketingOptIn,
    isSubmitting,
    submitError,
    submit,
  } = useAccountSetup(initialProfile);

  const handleContinue = async () => {
    const succeeded = await submit();
    if (succeeded) onComplete();
  };

  return (
    <Box
      css={{
        width: CARD_WIDTH,
        maxWidth: '100%',
        marginInline: 'auto',
        background: '$surfaceCardSecondary',
        boxShadow: '$card',
        borderRadius: '$medium',
      }}
    >
      <StackLayout
        spacing="$twoAndHalf"
        horizontalAlignment="stretch"
        css={{ paddingBlock: '$twoAndHalf', paddingInline: '$twoAndHalf' }}
      >
        <StackLayout
          spacing="$half"
          horizontalAlignment="center"
          css={{ maxWidth: '500px', marginInline: 'auto' }}
        >
          <HeadingText
            size="lg"
            level="2"
            as="h1"
            css={{ textAlign: 'center' }}
          >
            {formatMessage({ id: 'STR_ACCOUNT_SETUP_HEADING' })}
          </HeadingText>
          <ParagraphText css={{ textAlign: 'center' }}>
            {formatMessage({ id: 'STR_ACCOUNT_SETUP_DESCRIPTION' })}
          </ParagraphText>
        </StackLayout>

        <StackLayout spacing="$one" horizontalAlignment="center">
          <AvatarPicker
            placeholderSrc={avatarPlaceholderSrc}
            editBadgeSrc={avatarEditBadgeSrc}
          />
          {/* Read-only, plain text — the "visually distinct" field the AC requires. */}
          <ParagraphText
            css={{ fontSize: '20px', lineHeight: '28px', fontWeight: 500 }}
          >
            {fullName}
          </ParagraphText>
        </StackLayout>

        <StackLayout spacing="$oneAndHalf" horizontalAlignment="stretch">
          {fields.map((field) => (
            <AccountSetupField
              key={field.id}
              field={field}
              value={values[field.id] ?? ''}
              error={errors[field.id]}
              onChange={(value) => onFieldChange(field.id, value)}
            />
          ))}

          <Checkbox
            id="account-setup-marketing-opt-in"
            name="account-setup-marketing-opt-in"
            label={formatMessage({
              id: 'STR_ACCOUNT_SETUP_MARKETING_OPT_IN',
            })}
            checked={marketingOptIn}
            onChange={() => setMarketingOptIn((value) => !value)}
          />
        </StackLayout>

        <StackLayout
          spacing="$one"
          horizontalAlignment="stretch"
          css={{
            borderTop: '1px solid $onSurfaceBorderDefault',
            paddingTop: '$oneAndHalf',
          }}
        >
          {submitError != null && (
            <ParagraphText
              role="alert"
              css={{ color: '$onSurfaceTextCritical' }}
            >
              {formatMessage({ id: 'STR_ACCOUNT_SETUP_SUBMIT_ERROR' })}
            </ParagraphText>
          )}
          <StackLayout orientation="horizontal" horizontalAlignment="end">
            <Button
              type="button"
              priority="primary"
              size="large"
              loading={isSubmitting}
              disabled={isSubmitting}
              onClick={handleContinue}
            >
              {formatMessage({ id: 'STR_CONTINUE' })}
            </Button>
          </StackLayout>
        </StackLayout>
      </StackLayout>
    </Box>
  );
};
